<template>
  <div ref="viewportRef" class="book-canvas-viewport">
    <div ref="displayRef" class="book-canvas-display">
      <div ref="canvasRef" class="book-canvas"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
  // 直接按路径导入，避免走 @/hooks barrel 把 useExport(jspdf/html2canvas) 带进阅读端包
  import { useBook } from '@/hooks/useBook';
  import { useElementSize } from '@/hooks/useElementSize';
  import { computed, onMounted, ref } from 'vue';

  /** 嵌入编辑器预览时为 true：不创建水印（水印仅用于限制分享） */
  const props = defineProps<{
    embedded?: boolean;
  }>();

  const viewportRef = ref<HTMLDivElement | null>(null);
  const displayRef = ref<HTMLDivElement | null>(null);
  const canvasRef = ref<HTMLDivElement | null>(null);
  const {
    width: viewportWidth,
    height: viewportHeight,
    startObserve,
    remeasure
  } = useElementSize(viewportRef);

  const canvasSize = computed(() => ({
    width: viewportWidth.value,
    height: viewportHeight.value
  }));

  const displayScale = computed(() => 1);

  let resolveInitialSize!: (size: { width: number; height: number }) => void;
  let initialSizeResolved = false;
  const initialSizeReady = new Promise<{ width: number; height: number }>((resolve) => {
    resolveInitialSize = resolve;
  });

  const resolveInitialSizeIfReady = () => {
    if (initialSizeResolved || viewportWidth.value <= 0 || viewportHeight.value <= 0) return;

    initialSizeResolved = true;
    resolveInitialSize({
      width: viewportWidth.value,
      height: viewportHeight.value
    });
  };

  const getCanvasElement = () => displayRef.value;
  const getCanvasSize = () => canvasSize.value;

  onMounted(() => {
    startObserve(resolveInitialSizeIfReady);
    resolveInitialSizeIfReady();
  });

  const { getCanvas, canvasReady } = useBook(canvasRef, {
    embedded: !!props.embedded,
    waitForInitialSize: () => initialSizeReady
  });

  defineExpose({
    getCanvas,
    canvasReady,
    getCanvasElement,
    getCanvasSize,
    /** 全屏等布局突变后主动重测，避免仅等 ResizeObserver */
    remeasureCanvasSize: remeasure,
    canvasSize,
    displayScale
  });
</script>

<style scoped>
  .book-canvas-viewport {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .book-canvas-display {
    position: relative;
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
  }

  .book-canvas {
    position: absolute;
    inset: 0;
    background-color: #f8fafc;
    transform-origin: left top;
  }

  .book-canvas :deep(canvas) {
    display: block;
    position: absolute;
    left: 50%;
    top: 50%;
    width: 100%;
    height: 100%;
    transform: translate(-50%, -50%);
    touch-action: none;
  }

  @media (max-width: 720px), (hover: none) and (orientation: landscape),
    (pointer: coarse) and (orientation: landscape) {
    .book-canvas {
      background-color: #000;
    }
  }
</style>
